import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, Loader2Icon, LinkIcon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { EmailPreview } from '../components/EmailPreview';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { EmailBuilder } from '../components/builder/EmailBuilder';
import { EmailBlock, EmailGlobalStyles, EmailBuilderSchema, DEFAULT_EMAIL_STYLES } from '../components/builder/types';
import { emailTemplateLibraryApi, EmailLibraryRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { uploadImageToHost } from '../utils/api';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
const CATEGORIES = ['Confirmation', 'Welcome', 'Notification', 'Newsletter', 'Other'];
export function EmailTemplateLibraryEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showBuilderConfirm, setShowBuilderConfirm] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [templateData, setTemplateData] = useState({
    subject: '',
    preheader: '',
    html: ''
  });
  const [emailBuilderBlocks, setEmailBuilderBlocks] = useState<EmailBlock[]>([]);
  const [emailBuilderStyles, setEmailBuilderStyles] = useState<EmailGlobalStyles>(DEFAULT_EMAIL_STYLES);
  // Image hosting state
  const [isHostingImages, setIsHostingImages] = useState(false);
  const [hostingProgress, setHostingProgress] = useState('');
  const base64ImageCount = (templateData.html.match(/src=["']data:image\/[^"']+["']/gi) || []).length;
  const handleHostImages = async () => {
    const html = templateData.html;
    const base64Regex = /src=["'](data:image\/[^"']+)["']/gi;
    const matches: {
      full: string;
      dataUrl: string;
    }[] = [];
    let match;
    while ((match = base64Regex.exec(html)) !== null) {
      matches.push({
        full: match[0],
        dataUrl: match[1]
      });
    }
    if (matches.length === 0) {
      toast.info('No embedded base64 images found in the HTML');
      return;
    }
    setIsHostingImages(true);
    let updatedHtml = html;
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < matches.length; i++) {
      const {
        full,
        dataUrl
      } = matches[i];
      setHostingProgress(`Uploading image ${i + 1} of ${matches.length}...`);
      try {
        const typeMatch = dataUrl.match(/data:image\/(\w+)/);
        const ext = typeMatch ? typeMatch[1].replace('jpeg', 'jpg') : 'png';
        const filename = `email-lib-img-${Date.now()}-${i + 1}.${ext}`;
        const hostedUrl = await uploadImageToHost(dataUrl, filename);
        const quote = full.includes('"') ? '"' : "'";
        updatedHtml = updatedHtml.replace(full, `src=${quote}${hostedUrl}${quote}`);
        successCount++;
      } catch (err) {
        failCount++;
        console.warn(`Failed to host image ${i + 1}:`, err);
      }
    }
    setTemplateData(prev => ({
      ...prev,
      html: updatedHtml
    }));
    setIsHostingImages(false);
    setHostingProgress('');
    if (successCount > 0 && failCount === 0) {
      const saved = html.length - updatedHtml.length;
      toast.success(`${successCount} image${successCount > 1 ? 's' : ''} hosted successfully`, {
        description: `Saved ~${saved.toLocaleString()} characters. Remember to save.`,
        duration: 5000
      });
    } else if (successCount > 0) {
      toast.warning(`${successCount} hosted, ${failCount} failed`);
    } else {
      toast.error('Failed to host images', {
        description: 'The hosting server may not be available.',
        duration: 6000
      });
    }
  };
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      emailTemplateLibraryApi.get(id).then(record => {
        if (record) {
          setTemplateName(record.Template_Name || '');
          setDescription(record.Description || '');
          setCategory(record.Category || '');
          setTemplateData({
            subject: record.Email_Subject || '',
            preheader: record.Preheader_Text || '',
            html: record.Email_HTML_Body || ''
          });
          if (record.Builder_Mode === 'builder' && record.Builder_Schema) {
            try {
              const schema: EmailBuilderSchema = JSON.parse(record.Builder_Schema);
              setEmailBuilderBlocks(schema.blocks || []);
              setEmailBuilderStyles(schema.globalStyles || DEFAULT_EMAIL_STYLES);
              setEditorMode('builder');
            } catch {
              /* fallback to code */
            }
          }
        } else {
          toast.error('Template not found');
          navigate('/email-templates-library');
        }
      }).catch(err => {
        toast.error('Failed to load template', {
          description: err instanceof Error ? err.message : 'Unknown error'
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Template Name is required');
      return;
    }
    setIsSaving(true);
    const payload: Record<string, any> = {
      Template_Name: templateName,
      Description: description,
      Category: category,
      Email_HTML_Body: templateData.html,
      Email_Subject: templateData.subject,
      Preheader_Text: templateData.preheader,
      Builder_Mode: editorMode,
      Team: team
    };
    if (editorMode === 'builder') {
      payload.Builder_Schema = JSON.stringify({
        blocks: emailBuilderBlocks,
        globalStyles: emailBuilderStyles
      });
    }
    try {
      if (isEditing && id) {
        await emailTemplateLibraryApi.update(id, payload);
        toast.success('Email template updated');
      } else {
        await emailTemplateLibraryApi.create(payload);
        toast.success('Email template created');
        navigate('/email-templates-library');
      }
    } catch (err) {
      toast.error('Failed to save template', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsSaving(false);
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-3964">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3965" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-3966">Loading template...</span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3967">
      <div className="flex items-center justify-between" data-id="element-3968">
        <div className="flex items-center gap-4" data-id="element-3969">
          <Link to="/email-templates-library" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-3970">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-3971" />
          </Link>
          <div data-id="element-3972">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3973">
              {isEditing ? 'Edit Email Template' : 'Create Email Template'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-3974">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your email.' : 'Write email HTML for this template.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-3975">
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-3976">
            <button onClick={() => setEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3977">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && templateData.html && emailBuilderBlocks.length === 0) {
              setShowBuilderConfirm(true);
              return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3978">
              Builder
            </button>
          </div>
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-3979">
            <EyeIcon className="h-4 w-4" data-id="element-3980" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-3981">
            {isSaving ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-3982" /> : <SaveIcon className="h-4 w-4" data-id="element-3983" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-3984">
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-id="element-3985">
            <div className="grid grid-cols-2 gap-4" data-id="element-3986">
              <div data-id="element-3987">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3988">
                  Email Subject
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.subject} onChange={e => setTemplateData({
              ...templateData,
              subject: e.target.value
            })} placeholder="Email subject line" data-id="element-3989" />
              </div>
              <div data-id="element-3990">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3991">
                  Preheader Text
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.preheader} onChange={e => setTemplateData({
              ...templateData,
              preheader: e.target.value
            })} placeholder="Brief summary..." data-id="element-3992" />
              </div>
            </div>
          </div>

          <EmailBuilder initialBlocks={emailBuilderBlocks} initialStyles={emailBuilderStyles} onSchemaChange={(blocks, globalStyles) => {
        setEmailBuilderBlocks(blocks);
        setEmailBuilderStyles(globalStyles);
      }} onHtmlChange={html => {
        setTemplateData(prev => ({
          ...prev,
          html
        }));
      }} mergeVariables={['confirmation_link', 'unsubscribe_link', 'team_name', 'first_name', 'last_name', 'email']} data-id="element-3993" />

          <div className="grid grid-cols-3 gap-6" data-id="element-3994">
            <div className="col-span-2" data-id="element-3995" />
            <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} data-id="element-3996" />
          </div>
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-3997">
          <div className="col-span-2 space-y-6" data-id="element-3998">
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4" data-id="element-3999">
              <div className="grid grid-cols-2 gap-4" data-id="element-4000">
                <div className="space-y-2" data-id="element-4001">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4002">
                    Email Subject
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.subject} onChange={e => setTemplateData({
                ...templateData,
                subject: e.target.value
              })} placeholder="Email subject line" data-id="element-4003" />
                </div>
                <div className="space-y-2" data-id="element-4004">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-4005">
                    Preheader Text
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateData.preheader} onChange={e => setTemplateData({
                ...templateData,
                preheader: e.target.value
              })} placeholder="Brief summary..." data-id="element-4006" />
                </div>
              </div>
              <CodeEditor label="Email HTML Body" language="html" className="min-h-[400px]" charLimit={true} value={templateData.html} onChange={e => setTemplateData({
            ...templateData,
            html: e.target.value
          })} data-id="element-4007" />

              {/* Host Embedded Images action */}
              {base64ImageCount > 0 && <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-4008">
                  <div className="flex-1 min-w-0" data-id="element-4009">
                    <p className="text-sm font-medium text-amber-900" data-id="element-4010">
                      {base64ImageCount} embedded image
                      {base64ImageCount > 1 ? 's' : ''} detected
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-4011">
                      {isHostingImages ? hostingProgress : 'Base64 images bloat your HTML. Upload them to get short hosted URLs instead.'}
                    </p>
                  </div>
                  <button onClick={handleHostImages} disabled={isHostingImages} className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-60 whitespace-nowrap transition-colors" data-id="element-4012">
                    {isHostingImages ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-4013" /> : <LinkIcon className="h-4 w-4" data-id="element-4014" />}
                    {isHostingImages ? 'Hosting...' : 'Host Images'}
                  </button>
                </div>}
            </div>
            <GptCodeAssistant mode="email" currentCode={{
          html: templateData.html
        }} onCodeGenerated={code => {
          if (code.html) {
            setTemplateData(prev => ({
              ...prev,
              html: code.html
            }));
          }
        }} data-id="element-4015" />
          </div>
          <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} data-id="element-4016" />
        </div>}

      <PreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Template Preview" data-id="element-4017">
        <EmailPreview subject={templateData.subject} html={templateData.html} data-id="element-4018" />
      </PreviewModal>

      <ConfirmModal isOpen={showBuilderConfirm} title="Switch to Builder" message="The builder starts with a blank canvas — your existing code cannot be imported into the visual builder. However, your code is preserved and will still be there if you switch back to Code mode without saving." confirmLabel="Switch to Builder" variant="warning" onCancel={() => setShowBuilderConfirm(false)} onConfirm={() => {
      setShowBuilderConfirm(false);
      setEditorMode('builder');
    }} data-id="element-4019" />
    </div>;
}
function SettingsPanel({
  templateName,
  setTemplateName,
  description,
  setDescription,
  category,
  setCategory,
  team
}: {
  templateName: string;
  setTemplateName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  team: string;
}) {
  return <div className="space-y-6" data-id="element-4020">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-4021">
        <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4022">
          Template Settings
        </h3>
        <div className="space-y-2" data-id="element-4023">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4024">
            Template Name
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="My Email Template" data-id="element-4025" />
        </div>
        <div className="space-y-2" data-id="element-4026">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4027">
            Description
          </label>
          <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what this template is for..." data-id="element-4028" />
        </div>
        <div className="space-y-2" data-id="element-4029">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4030">
            Category
          </label>
          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={category} onChange={e => setCategory(e.target.value)} data-id="element-4031">
            <option value="" data-id="element-4032">Select category...</option>
            {CATEGORIES.map(c => <option key={c} value={c} data-id="element-4033">
                {c}
              </option>)}
          </select>
        </div>
        <div className="space-y-2" data-id="element-4034">
          <label className="block text-sm font-medium text-gray-700" data-id="element-4035">
            Team
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4036" />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-4037">
        <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-4038">
          Available Variables
        </h3>
        <p className="text-xs text-gray-500" data-id="element-4039">
          Use these merge tags in your email HTML. They'll be replaced with
          actual values when the email is sent.
        </p>
        <div className="flex flex-wrap gap-1.5" data-id="element-4040">
          {['confirmation_link', 'unsubscribe_link', 'team_name', 'first_name', 'last_name', 'email'].map(v => <button key={v} onClick={() => {
          const text = `{{${v}}}`;
          if (navigator.clipboard) {
            navigator.clipboard.writeText(text);
            toast.success(`Copied ${text}`, {
              duration: 1500
            });
          }
        }} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100" title={`Copy {{${v}}}`} data-id="element-4041">
              {`{{${v}}}`}
            </button>)}
        </div>
      </div>
    </div>;
}